const DEFAULT_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://femtyz-admin.pixoatic.in/";

const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

const inflightGetRequests = new Map();

const AUTH_FLOW_PATHS = new Set([
  "api/login",
  "api/signup",
  "api/verify_otp",
  "api/resend_otp",
  "api/forgot_password",
  "api/reset_password",
]);

let authExpiredNotified = false;

export function isAuthErrorMessage(text) {
  return /invalid or expired token|unauthorized|jwt expired|token expired/i.test(String(text || ""));
}

export function isAuthErrorResponse({ status, data } = {}) {
  if (status === 401 || status === 403) return true;
  const errors = Array.isArray(data?.errors) ? data.errors : [];
  const message = String(data?.message || "");
  const combined = [...errors.map((e) => String(e)), message].join(" ");
  return isAuthErrorMessage(combined);
}

function isAuthFlowPath(path) {
  const normalized = String(path || "").replace(/^\//, "").split("?")[0].toLowerCase();
  return AUTH_FLOW_PATHS.has(normalized);
}

function notifyAuthExpired(data) {
  if (authExpiredNotified) return;
  authExpiredNotified = true;
  window.setTimeout(() => {
    authExpiredNotified = false;
  }, 4000);

  const errors = Array.isArray(data?.errors) ? data.errors : [];
  const message =
    errors.filter(Boolean).join("\n") ||
    String(data?.message || "").trim() ||
    "Invalid or expired token";

  window.dispatchEvent(
    new CustomEvent("femtyz:auth-expired", {
      detail: { message },
    })
  );
}

function getToken() {
  try {
    return localStorage.getItem("femtyz_token");
  } catch {
    return null;
  }
}

function buildNetworkErrorResult({ timedOut = false, cancelled = false } = {}) {
  if (cancelled) {
    return {
      ok: false,
      status: 0,
      data: { message: "Request cancelled.", aborted: true },
    };
  }

  if (timedOut) {
    return {
      ok: false,
      status: 0,
      data: {
        message: "Connection timed out. Please try again.",
        networkError: true,
        timedOut: true,
      },
    };
  }

  return {
    ok: false,
    status: 0,
    data: {
      message: "Unable to reach the server. Please check your connection and try again.",
      networkError: true,
    },
  };
}

export async function apiRequest(
  path,
  {
    method = "GET",
    body,
    headers,
    token: tokenOverride,
    signal,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
  } = {}
) {
  const token = tokenOverride !== undefined ? tokenOverride : getToken();
  const normalizedMethod = String(method || "GET").toUpperCase();
  const url = `${DEFAULT_BASE_URL}${path}`;

  const isDedupableGet = normalizedMethod === "GET" && !body && !signal;
  const inflightKey = isDedupableGet
    ? `${normalizedMethod} ${url} :: ${token ? "auth" : "anon"}`
    : "";

  if (isDedupableGet && inflightGetRequests.has(inflightKey)) {
    return inflightGetRequests.get(inflightKey);
  }

  const requestPromise = (async () => {
    const timeoutController = new AbortController();
    let timedOut = false;
    let externalAbortHandler = null;

    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      timeoutController.abort();
    }, timeoutMs);

    if (signal) {
      if (signal.aborted) {
        window.clearTimeout(timeoutId);
        return buildNetworkErrorResult({ cancelled: true });
      }
      externalAbortHandler = () => timeoutController.abort();
      signal.addEventListener("abort", externalAbortHandler, { once: true });
    }

    try {
      const res = await fetch(url, {
        method: normalizedMethod,
        signal: timeoutController.signal,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(headers || {}),
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = await res.json().catch(() => ({}));
      const result = { ok: res.ok, status: res.status, data };

      if (
        token &&
        !isAuthFlowPath(path) &&
        !result.ok &&
        isAuthErrorResponse(result)
      ) {
        notifyAuthExpired(data);
      }

      return result;
    } catch (error) {
      if (error?.name === "AbortError") {
        if (timedOut) {
          return buildNetworkErrorResult({ timedOut: true });
        }
        return buildNetworkErrorResult({ cancelled: true });
      }

      return buildNetworkErrorResult();
    } finally {
      window.clearTimeout(timeoutId);
      if (signal && externalAbortHandler) {
        signal.removeEventListener("abort", externalAbortHandler);
      }
    }
  })();

  if (isDedupableGet) {
    inflightGetRequests.set(inflightKey, requestPromise);
    requestPromise.finally(() => {
      inflightGetRequests.delete(inflightKey);
    });
  }

  return requestPromise;
}

export const API_BASE_URL = DEFAULT_BASE_URL;

export function getAuthHeader() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
